import { useState, useMemo } from 'react';
import sensorTopicsService from '../services/sensorTopicsService';

export const useSensorTopics = (unitId = null) => {
  const [loading] = useState(false);
  const [error, setError] = useState(null);

  // Get all available sensors
  const allSensors = useMemo(() => {
    try {
      return sensorTopicsService.getAllTopics();
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, []);

  // Get sensors with labels for display
  const sensorsWithLabels = useMemo(() => {
    try {
      return sensorTopicsService.getSensorsWithLabels();
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, []);

  // Get grouped sensors
  const groupedSensors = useMemo(() => {
    try {
      return sensorTopicsService.getGroupedSensors();
    } catch (err) {
      setError(err.message);
      return { color3bands: [], individual: [] };
    }
  }, []);

  // Get MQTT topics for a specific unit
  const getMqttTopicsForUnit = useMemo(() => {
    if (!unitId) return () => [];
    
    return (sensorName = null) => {
      try {
        return sensorTopicsService.getMqttTopics(unitId, sensorName);
      } catch (err) {
        setError(err.message);
        return [];
      }
    };
  }, [unitId]);

  // Helper functions
  const getSensorConfig = (sensorName) => {
    try {
      return sensorTopicsService.getSensorConfig(sensorName);
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const getConfigByTopic = (topic) => {
    try {
      return sensorTopicsService.getConfigByTopic(topic);
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  // Add the new data format functions
  const getSensorDataType = (sensorName) => {
    try {
      return sensorTopicsService.getSensorDataType(sensorName);
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const getSensorDataFormat = (sensorName) => {
    try {
      return sensorTopicsService.getSensorDataFormat(sensorName);
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const getTopicDataFormat = (topic) => {
    try {
      return sensorTopicsService.getTopicDataFormat(topic);
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  // Extract sensor information from MQTT topic pattern
  const extractSensorFromTopic = (topic) => {
    try {
      // Parse topics like 'unitId/nodeId/data/sensorTopic'
      const parts = topic.split('/');
      if (parts.length >= 4 && parts[2] === 'data') {
        const sensorTopic = parts[3];
        return getConfigByTopic(sensorTopic);
      }
      return null;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const hasSensor = (sensorName) => {
    return sensorTopicsService.hasSensor(sensorName);
  };

  return {
    allSensors,
    sensorsWithLabels,
    groupedSensors,
    getMqttTopicsForUnit,
    getSensorConfig,
    getConfigByTopic,
    hasSensor,
    // Include new functions in the returned object
    getSensorDataType,
    getSensorDataFormat,
    getTopicDataFormat,
    extractSensorFromTopic,
    loading,
    error,
    sensorCount: allSensors.length
  };
};